import type { QueryClient } from '@tanstack/react-query';
import { fetchUserEntityClaims } from '../services/entityApi';
import type { Logger } from '../types';

export function activeEntityClaimsQueryKey(userUuid: string, entityUuid: string, domain: string) {
  return ['activeEntityClaims', userUuid, entityUuid, domain] as const;
}

/**
 * Upper bound on the Policy API call. This query runs in parallel with the
 * entitlement queries behind the shell's full-page loader, so a hung Policy
 * API must not hold the whole shell: after this long it fails closed.
 */
export const CLAIMS_FETCH_TIMEOUT_MS = 3000;

/**
 * A signal that aborts when `outer` aborts (react-query cancelled the query) or
 * after `ms` (TimeoutError). Built by hand rather than with `AbortSignal.any`,
 * which older Safari/Chrome/Firefox lack: a TypeError there would fail closed on
 * every load and silently hide claim-gated items for those users.
 */
function timeoutSignal(outer: AbortSignal, ms: number) {
  const controller = new AbortController();
  const onOuterAbort = () => controller.abort(outer.reason);
  if (outer.aborted) onOuterAbort();
  else outer.addEventListener('abort', onOuterAbort, { once: true });
  const timer = setTimeout(
    () => controller.abort(new DOMException('Claims request timed out', 'TimeoutError')),
    ms
  );
  return {
    signal: controller.signal,
    dispose: () => {
      clearTimeout(timer);
      outer.removeEventListener('abort', onOuterAbort);
    },
  };
}

/** Rejects with the signal's reason as soon as it aborts, else settles with `promise`. */
function abortable<T>(promise: Promise<T>, signal: AbortSignal): Promise<T> {
  if (signal.aborted) return Promise.reject(signal.reason);
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(signal.reason);
    signal.addEventListener('abort', onAbort, { once: true });
    promise.then(resolve, reject).finally(() => signal.removeEventListener('abort', onAbort));
  });
}

// Recheck periodically so a transient failure isn't stuck at the fail-closed
// empty value for the rest of the session — only an entity switch or logout
// would otherwise create a new query key and retry.
const RECOVERY_POLL_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Claim keys the user holds at the active entity in one claim domain, used to
 * show or hide claim-gated menu items (see `requiredClaimPrefixes` on app
 * configs). Never throws: a timeout, transport or auth failure resolves to an
 * empty list, which hides claim-gated items without affecting anything else.
 */
export function activeEntityClaimsQueryOptions({
  userUuid,
  entityUuid,
  domain,
  getAccessTokenSilently,
  logger,
  queryClient,
}: {
  userUuid: string;
  entityUuid: string;
  domain: string;
  getAccessTokenSilently: () => Promise<string>;
  logger: Logger;
  queryClient: QueryClient;
}) {
  const queryKey = activeEntityClaimsQueryKey(userUuid, entityUuid, domain);

  return {
    queryKey,
    queryFn: async ({ signal }: { signal: AbortSignal }): Promise<string[]> => {
      // The timeout covers the token fetch too: a hung silent refresh must not
      // hold the shell's full-page loader any longer than the API call could.
      const timeout = timeoutSignal(signal, CLAIMS_FETCH_TIMEOUT_MS);
      try {
        const token = await abortable(getAccessTokenSilently(), timeout.signal);
        return await fetchUserEntityClaims(
          userUuid,
          entityUuid,
          domain,
          token,
          { signal: timeout.signal },
          logger
        );
      } catch (error) {
        // react-query cancelled the query (entity switch, unmount): not a
        // failure, so don't log it or fail closed — let react-query discard it.
        if (signal.aborted) throw error;
        // A refetch failure must not overwrite a known-good answer: rethrow so
        // react-query keeps the previous data (and retries on the next poll).
        // Only the very first fetch fails closed to [].
        const previous = queryClient.getQueryData(queryKey);
        if (previous !== undefined) throw error;
        logger.warn('Failed to fetch user entity claims; failing closed to []', {
          entity_uuid: entityUuid,
          domain,
          error_message: error instanceof Error ? error.message : String(error),
        });
        return [];
      } finally {
        timeout.dispose();
      }
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchInterval: RECOVERY_POLL_INTERVAL_MS,
    retry: false,
  };
}
